import { FC } from 'react'
import { FileText, X, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react'
import { cn } from '../../lib/utils'
import { UnifiedFile } from '../../contexts/ChatContext'

interface DocumentCardProps {
  document: UnifiedFile
  onRemove: (id: string) => void
}

/**
 * Compact document card showing upload/parse status
 * Designed to sit in a horizontal row above the chat
 */
export const DocumentCard: FC<DocumentCardProps> = ({ document, onRemove }) => {
  const { id, name, status, progress, parsingStatus } = document

  // Map status for display - combine upload status and parsing status
  const displayStatus = status === 'uploading' ? 'uploading'
    : status === 'processing' ? 'processing'
    : parsingStatus === 'pending' || parsingStatus === 'quick_done' ? 'parsing'
    : status === 'ready' ? 'ready'
    : status === 'error' ? 'error' : 'pending'

  // Truncate filename if too long
  const displayName = name.length > 20
    ? `${name.slice(0, 12)}...${name.slice(-6)}`
    : name

  return (
    <div
      className={cn(
        "group relative flex items-center gap-2.5 pl-3 pr-2 py-2 rounded-lg border transition-all duration-300",
        "bg-card/80 backdrop-blur-sm",
        (displayStatus === 'uploading' || displayStatus === 'processing' || displayStatus === 'parsing') && "border-primary/40 shadow-sm shadow-primary/10",
        displayStatus === 'ready' && "border-emerald-500/40 bg-emerald-500/5",
        displayStatus === 'error' && "border-destructive/40 bg-destructive/5",
        displayStatus === 'pending' && "border-border/50"
      )}
    >
      {/* Icon */}
      <div className={cn(
        "flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center transition-colors",
        (displayStatus === 'uploading' || displayStatus === 'processing' || displayStatus === 'parsing') && "bg-primary/10",
        displayStatus === 'ready' && "bg-emerald-500/10",
        displayStatus === 'error' && "bg-destructive/10",
        displayStatus === 'pending' && "bg-muted"
      )}>
        {displayStatus === 'uploading' || displayStatus === 'processing' || displayStatus === 'parsing' ? (
          <Loader2 className="h-4 w-4 text-primary animate-spin" />
        ) : displayStatus === 'ready' ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
        ) : displayStatus === 'error' ? (
          <AlertCircle className="h-4 w-4 text-destructive" />
        ) : (
          <FileText className="h-4 w-4 text-muted-foreground" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium truncate" title={name}>
            {displayName}
          </span>
          {displayStatus === 'ready' && (
            <span className="text-[10px] font-medium text-emerald-600 bg-emerald-500/10 px-1.5 py-0.5 rounded">
              Ready
            </span>
          )}
        </div>

        {/* Progress bar for uploading/parsing state */}
        {(displayStatus === 'uploading' || displayStatus === 'processing' || displayStatus === 'parsing') && (
          <div className="mt-1.5 flex items-center gap-2">
            <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary to-primary/70 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${Math.max(progress, 5)}%` }}
              />
            </div>
            <span className="text-[10px] font-mono text-muted-foreground w-8">
              {progress}%
            </span>
          </div>
        )}

        {/* Status text */}
        {displayStatus === 'uploading' && (
          <p className="text-[10px] text-muted-foreground mt-0.5">
            Uploading...
          </p>
        )}
        {(displayStatus === 'processing' || displayStatus === 'parsing') && (
          <p className="text-[10px] text-muted-foreground mt-0.5">
            Extracting clinical data...
          </p>
        )}
        {displayStatus === 'error' && (
          <p className="text-[10px] text-destructive mt-0.5">
            {document.error || 'Failed to process'}
          </p>
        )}
      </div>

      {/* Remove button */}
      <button
        onClick={() => onRemove(id)}
        className={cn(
          "flex-shrink-0 p-1 rounded-md transition-all",
          "opacity-0 group-hover:opacity-100",
          "hover:bg-muted text-muted-foreground hover:text-foreground",
          "focus:outline-none focus:ring-2 focus:ring-primary/20 focus:opacity-100"
        )}
        title="Remove document"
        aria-label={`Remove ${name}`}
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>

      {/* Parsing glow effect */}
      {(displayStatus === 'uploading' || displayStatus === 'processing' || displayStatus === 'parsing') && (
        <div className="absolute inset-0 rounded-lg bg-primary/5 animate-pulse pointer-events-none" />
      )}
    </div>
  )
}


